/**
 * The player's stepper (plan docs/plans/lms-plan.md 7.2): Overview, Slides,
 * Reading, then Quiz (or Acknowledge for a read_ack course or an acknowledge
 * assignment, 8.1, 11.3), and Done once the course is complete.
 *
 * Each step is a button; a locked step is disabled and says why (the lock
 * reason is its title and its screen-reader text), so the gates of 7.2 are
 * visible rather than silent. aria-current="step" marks the stage shown.
 */
import { Check, Lock } from 'lucide-react';
import type { PlayerFlow, PlayerStage } from '../../hooks/usePlayer';

export interface StepperProps {
  flow: PlayerFlow;
  stage: PlayerStage;
  /** Whether the step is complete (a tick). */
  complete: (stage: PlayerStage) => boolean;
  /** Why a step is locked, or null. */
  lockOf: (stage: PlayerStage) => string | null;
  showDone: boolean;
  onSelect: (stage: PlayerStage) => void;
}

const LABELS: Record<PlayerStage, string> = {
  overview: 'Overview',
  slides: 'Slides',
  reading: 'Reading',
  quiz: 'Quiz',
  acknowledge: 'Acknowledge',
  done: 'Done',
};

export function stepsFor(flow: PlayerFlow, showDone: boolean): PlayerStage[] {
  const steps: PlayerStage[] = ['overview', 'slides', 'reading', flow === 'acknowledge' ? 'acknowledge' : 'quiz'];
  if (showDone) steps.push('done');
  return steps;
}

export function Stepper({ flow, stage, complete, lockOf, showDone, onSelect }: StepperProps) {
  const steps = stepsFor(flow, showDone);
  return (
    <nav aria-label="Course steps" data-testid="lms-player-stepper" className="overflow-x-auto">
      <ol className="flex min-w-max items-center gap-1 sm:gap-2">
        {steps.map((step, i) => {
          const active = step === stage;
          const lock = lockOf(step);
          const done = complete(step);
          const badgeCls = active
            ? 'bg-primary-600 text-white dark:bg-primary-500'
            : done
            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
          return (
            <li key={step} className="flex items-center gap-1 sm:gap-2">
              {i > 0 && <span aria-hidden="true" className="h-px w-3 bg-gray-300 dark:bg-gray-600 sm:w-6" />}
              <button
                type="button"
                data-testid={`lms-step-${step}`}
                aria-current={active ? 'step' : undefined}
                disabled={Boolean(lock)}
                title={lock ?? undefined}
                onClick={() => onSelect(step)}
                className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0 sm:py-1.5 ${
                  active
                    ? 'text-primary-700 dark:text-primary-400'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <span className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${badgeCls}`}>
                  {lock ? (
                    <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : done && !active ? (
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    i + 1
                  )}
                </span>
                <span>{LABELS[step]}</span>
                {done && !active && <span className="sr-only">(complete)</span>}
                {lock && <span className="sr-only">{`(locked: ${lock})`}</span>}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Stepper;
